// #region Imports
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import AvatarUpload, { AvatarUploadValue } from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import ProfileNameInput from "marketplace/_views/profiles/profile-name-input";
import VisibilityToggleSwitch, {
  buildVisibilityConfirmMessage,
  buildVisibilityConfirmTitle,
  buildVisibilitySuccessMessage,
} from "marketplace/_views/profiles/visibility-toggle-switch";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { TeamJSON } from "marketplace/teams/_schema/team-schema";
import {
  useCancelTeamInvitationMutation,
  useCreateTeamAvatarMutation,
  useCreateTeamInvitationMutation,
  useCreateTeamMutation,
  useGetTeamAvatarQuery,
  useGetTeamByIdQuery,
  useGetTeamInvitationsByTeamIdQuery,
  usePromoteTeamMemberToLeaderMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMutation,
} from "marketplace/teams/_api/marketplace-team-api";
import {
  getTeamAvatar,
  getTeamById,
  getTeamInvitationsByTeamId,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import { buildUpdateMarketplaceTeamPayload } from "marketplace/teams/_api/requests-contracts/update-marketplace-team-api-request-schema";
import ManageTeamFeeSchedules from "marketplace/teams/fee-schedules/_views/manage-team-fee-schedules";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import TeamProfile from "marketplace/team-profile/team-profile";
import EditTeam from "marketplace/team-profile/_views/edit-team";
import { skipToken } from "@reduxjs/toolkit/query";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  // The dialog hands the form a bounded height, so the fields scroll inside it and the save button
  // keeps a row of its own at the bottom instead of floating over whatever is under it.
  maxHeight: "100%",
  minHeight: 0,
  width: "100%",
}));
const FormScrollArea = styled("div")(({ theme }) => ({
  display: "grid",
  flex: "1 1 auto",
  gridTemplateRows: "repeat(5, auto)",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  // MUI hangs a switch's control 11px outside its own row, so that the track lines up with the body
  // text beside it. This area hides its overflow, which cut the hover halo off at the edge — so the
  // room the control is designed to hang into is given here, where every row shares it and the
  // alignment the negative margin buys is kept.
  paddingLeft: theme.spacing(1.5),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridRow: "1",
  justifyContent: "space-between",
  padding: theme.spacing(0, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const AboutYou = styled("section")(({ theme }) => ({
  gridRow: "3",
  marginTop: theme.spacing(1),
}));
const AboutYouTextField = styled(TextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  flex: 1,
  width: "100%",
}));
/** Its own block between the description and the switch, spaced to read as a section of the form. */
const FeeSchedulesSection = styled("section")(({ theme }) => ({
  gridRow: "4",
  marginTop: theme.spacing(3),
}));
const FeeSchedulesHint = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));
const TeamDetails = styled("section")(({ theme }) => ({
  gridRow: "5",
  // Far enough from the fee schedule above to read as its own row rather than part of that section,
  // and clear of the button row's divider below.
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(3),
}));
const EditTeamMembers = styled(EditTeam)(() => ({
  gridRow: "2",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flex: "0 0 auto",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  padding: theme.spacing(2, 0),
  width: "100%",
}));
const SaveButtonWrapper = styled("span")(() => ({
  alignSelf: "flex-start",
  display: "inline-flex",
}));
const SaveValidationMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
// #endregion

// #region Component Props
export type EditTeamProfileProps = {
  isVisible?: boolean;
  teamId?: Guid;
  profile?: TeamProfile;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (team: Team) => void;
};
// #endregion

export default function EditTeamProfile(props: Readonly<EditTeamProfileProps>) {
  const { navigate, searchParams, setSearchParams, onFormDirty, onSave } = props;

  // #region Hooks
  const session = useSession();
  const confirm = useConfirmDialog();

  const initialNewTeamLeader = useMemo(() => {
    if (props.teamId || props.profile) return undefined;

    const individualId = session.user!.individualId;
    const legalEntityId = session.user!.legalEntityId;
    const userId = session.user!.id;
    const firstName = session.user?.name?.firstName;
    const lastName = session.user?.name?.lastName;

    if (!individualId) throw new Error("Individual not found");
    if (!legalEntityId) throw new Error("Entity not found");
    if (!userId) throw new Error("User not found");
    if (!firstName) throw new Error("Individual first name not found");
    if (!lastName) throw new Error("Individual last name not found");

    return Individual.create({
      id: individualId,
      userId,
      legalEntityId,
      profile: IndividualProfile.create({ userId, firstName, lastName }),
      isVisible: true,
    });
  }, [props.profile, props.teamId, session.user]);

  const initialProfile = useMemo(() => {
    if (props.profile) return props.profile;
    if (props.teamId) return undefined;

    return new TeamProfile({
      name: Config.defaultProfileName,
      categories: [],
      description: "",
    });
  }, [props.profile, props.teamId]);

  // #region State
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean | undefined>(props.isVisible);

  const [teamId, setTeamId] = useState<Guid | undefined>(props.teamId);
  const [profile, setProfile] = useState<TeamProfile | undefined>(initialProfile);
  const [updatedAvatar, setUpdatedAvatar] = useState<AvatarUploadValue | string | undefined | null>();

  const [formTeamName, setFormTeamName] = useState<string>(initialProfile?.name ?? Config.defaultProfileName);
  const [formCategories, setFormCategories] = useState<string[]>(initialProfile?.categories ?? []);
  const [formDescription, setFormDescription] = useState<string>(initialProfile?.description ?? "");
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<Individual | undefined>(initialNewTeamLeader);
  const [promotingMemberToLeader, setPromotingMemberToLeader] = useState<boolean>(false);
  const [existingTeamMembers, setExistingTeamMembers] = useState<Individual[]>([]);
  const [invitedMembers, setInvitedMembers] = useState<Individual[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [editedInvitations, setEditedInvitations] = useState<TeamInvitation[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<Individual[]>([]);
  const [profileHash, setProfileHash] = useState<string>(() => initialProfile ? JSON.stringify(initialProfile.toJSON()) : "");

  const [updateTeam] = useUpdateTeamMutation();
  const [createTeam] = useCreateTeamMutation();
  const [createTeamAvatar] = useCreateTeamAvatarMutation();
  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const [promoteTeamMember] = usePromoteTeamMemberToLeaderMutation();
  const [cancelTeamInvitation] = useCancelTeamInvitationMutation();
  const [createTeamInvitation] = useCreateTeamInvitationMutation();

  const teamQuery = useGetTeamByIdQuery(teamId ? { teamId: teamId.value } : { teamId: "" }, { skip: !teamId });
  const invitationsQuery = useGetTeamInvitationsByTeamIdQuery(
    teamId ? { teamId: teamId.value, open: true } : skipToken,
    { skip: !teamId },
  );

  // #region Selectors
  const team = useSelector(getTeamById(teamId));

  /**
   * The leader is the only account the fee schedule API lets write a team's rates.
   *
   * Checked here rather than left to the route: `TeamProfilesPage` only applies this test when the
   * profiles table opens a row, so a member reaching `/marketplace/profiles/teams/edit/:teamId`
   * directly still gets this form. Without it they would be offered add, edit and delete controls
   * whose every write comes back `403`.
   */
  const isTeamLeader = !!team?.leader?.userId?.isEqualTo(session.user?.id);
  const { isLoading: isLoadingTeam, error: errorLoadingTeam } = teamQuery;
  const teamAvatar = useSelector(getTeamAvatar(teamId ?? null, "small"));
  useGetTeamAvatarQuery(
    teamId ? { teamId: teamId.value, size: "small" } : skipToken,
    { skip: !team?.profile?.avatarId || !!teamAvatar },
  );
  const teamInvitations = useSelector(getTeamInvitationsByTeamId(teamId));
  const { isLoading: isLoadingTeamInvitations, error: errorLoadingTeamInvitations } = invitationsQuery;

  useEffect(() => {
    if (!teamId || teamInvitations || isLoadingTeamInvitations || errorLoadingTeamInvitations) {
      return;
    }
    // Invitations loaded via useGetTeamInvitationsByTeamIdQuery above
  }, [errorLoadingTeamInvitations, isLoadingTeamInvitations, teamId, teamInvitations]);


  const displayedInvitations = teamInvitations?.map(
    (invitation) => editedInvitations.find((editedInvitation) => editedInvitation.id.isEqualTo(invitation.id)) ?? invitation,
  );



  // #region Validation
  const isProfileTypeSelected = useMemo(() => {
    return Boolean(profile?.categories && profile.categories.length > 0);
  }, [profile?.categories]);

  const isProfileValidated = useMemo(() => {
    if (!profile) return false;
    const trimmedName = profile.name.trim();
    const hasName: boolean = trimmedName.length > 0 && trimmedName !== Config.defaultProfileName;
    return hasName && isProfileTypeSelected;
  }, [isProfileTypeSelected, profile]);

  const isFormDirty = useMemo((): boolean => {
    if (!profile) return false;
    if (pendingRemovals.length > 0) return true;
    if (invitedMembers.length > 0) return true;
    if (pendingInvitations.length > 0) return true;
    if (editedInvitations.length > 0) return true;
    if (promotingMemberToLeader) return true;
    if (updatedAvatar && typeof updatedAvatar === "object") return true;

    const hash = JSON.stringify(profile.toJSON());
    return hash !== profileHash;
  }, [
    editedInvitations,
    invitedMembers,
    pendingInvitations,
    pendingRemovals,
    profile,
    profileHash,
    promotingMemberToLeader,
    updatedAvatar,
  ]);

  const displayedIsVisible = isVisible ?? team?.isVisible ?? false;

  /**
   * Visibility is a field of this form now rather than a switch that writes on its own, so it has to
   * count towards whether there is anything to save.
   *
   * Deliberately beside the memo above and not inside it: that memo's manual memoization is still
   * preserved here, and reworking its dependencies is what costs a component the whole thing.
   */
  const isVisibilityDirty = Boolean(teamId) && displayedIsVisible !== (team?.isVisible ?? false);
  const hasUnsavedChanges = isFormDirty || isVisibilityDirty;

  /**
   * What the form still needs before it can be saved, so a disabled button is not silent about why.
   * Kept in the form's own reading order: name, then profile type.
   *
   * An untouched form says nothing. Save is disabled until something changes, but that is the state
   * every form opens in — telling someone there is nothing to save before they have typed anything
   * reads as an error against work they have not started.
   */
  function buildSaveValidationMessage(): string {
    if (!profile) return "";
    const trimmedName = profile.name.trim();
    if (trimmedName.length < 1) return "Give the team profile a name to enable save.";
    if (trimmedName === Config.defaultProfileName) {
      return `Replace the default name "${Config.defaultProfileName}" to enable save.`;
    }
    if (!isProfileTypeSelected) return "Select at least one profile type to enable save.";
    return "";
  }
  const saveValidationMessage = buildSaveValidationMessage();

  // #region Lifecycle
  useEffect(() => {
    if (!team?.profile) return;

    const nextProfileHash = JSON.stringify(team.profile?.toJSON());
    if (profileHash === nextProfileHash) return;

    let active = true;
    queueMicrotask(() => {
      if (!active) return;

      setProfile(team.profile);
      setIsVisible(team.isVisible ?? false);
      setFormTeamName(team.profile.name);
      setSelectedTeamLeader(team.leader);
      setExistingTeamMembers(team.members);
      setFormCategories(team.profile.categories ?? []);
      setFormDescription(team.profile.description ?? "");
      setProfileHash(nextProfileHash);
    });

    return () => {
      active = false;
    };
  }, [team, profileHash]);

  useEffect(() => {
    if (errorLoadingTeam) {
      enqueueSnackbar("Profile currently unavailable", { variant: "warning" });
    }
  }, [errorLoadingTeam]);
  // #endregion

  useEffect(() => {
    onFormDirty?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onFormDirty]);

  // #region Functions

  function constructTeam(): Team {
    if (!profile) throw new Error("Updated Team profile not found");
    const teamLeader = selectedTeamLeader ?? team?.leader;
    if (!teamLeader) throw new Error("Team leader not found");

    return new Team({
      leader: teamLeader,
      profile: profile,
      isVisible: displayedIsVisible,
      associatedFirms: [],
      members: existingTeamMembers.length ? existingTeamMembers : team?.members ?? [],
      id: teamId ?? Guid.generate(),
    });
  }

  async function handleSaveButtonClick() {
    let updatedTeam: Team | undefined;
    const wasVisibilityChanged = isVisibilityDirty;
    const profileName = team?.profile?.name ?? "this";

    // Before the try: a confirm that reads `??` or returns early inside one is a compiler bailout.
    const maySave = await confirmVisibilityChange();
    if (!maySave) return;

    try {
      setIsSaving(true);

      if (teamId) {
        await removeMembers(teamId);
        await inviteMembers(teamId);
        await updateInvitationMessages(teamId);
        if (promotingMemberToLeader) await promoteMemberToLeader(teamId);
        updatedTeam = await updateProfile();
      } else {
        updatedTeam = await createProfile();
      }
      if (!updatedTeam.id) throw new Error("Failed to save profile");

      setInvitedMembers([]);
      setPendingInvitations([]);
      setEditedInvitations([]);
      setPendingRemovals([]);
      setUpdatedAvatar(undefined);

      setTeamId(updatedTeam.id);
      setIsVisible(updatedTeam.isVisible ?? false);
      setProfileHash(JSON.stringify(updatedTeam.profile.toJSON()));

      enqueueSnackbar("Profile changes saved", { variant: "success" });
      if (wasVisibilityChanged) {
        enqueueSnackbar(buildVisibilitySuccessMessage(profileName, updatedTeam.isVisible === true), {
          variant: "info",
        });
      }
      onSave?.(updatedTeam);
    } catch {
      enqueueSnackbar("Unable to save profile", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  async function updateProfile(): Promise<Team> {
    try {
      if (!isProfileValidated) throw new Error("Profile is not valid");
      if (!team) throw new Error("Team not found");
      if (!profile) throw new Error("Updated vendor team profile not found");

      const updatedTeam: Team = constructTeam();
      if (updatedAvatar && typeof updatedAvatar === "object") {
        await createAvatar(updatedTeam.id, updatedAvatar);
      }
      // Member, invitation, leader, and avatar mutations are committed by their own endpoints.
      // Do not follow them with an empty profile PATCH: the API rejects [] and would turn an
      // otherwise successful save into an error (and invite/member changes would be replayed on
      // the next attempt).
      const profilePatch = buildUpdateMarketplaceTeamPayload(team, updatedTeam);
      if (profilePatch.length === 0) return updatedTeam;

      const teamResponseJson: TeamJSON = await updateTeam({ teamId: updatedTeam.id.value, originalTeam: team, updatedTeam }).unwrap();
      if (!teamResponseJson.id) throw new Error("Unable to update vendor team");

      const teamResponse = Team.fromJSON(teamResponseJson);
      setProfile(teamResponse.profile);
      return teamResponse;
    } catch {
      throw new Error("Unable to update profile");
    }
  }

  async function createProfile(): Promise<Team> {
    try {
      if (!isProfileValidated) throw new Error("Profile is not valid");

      const teamToCreate: Team = constructTeam();
      const createdTeamJson: TeamJSON = await createTeam({ team: teamToCreate }).unwrap();

      if (!createdTeamJson.id) throw new Error("Unable to create vendor team");

      const createdTeam = Team.fromJSON(createdTeamJson);
      setProfile(createdTeam.profile);
      if (updatedAvatar && typeof updatedAvatar === "object") await createAvatar(createdTeam.id, updatedAvatar);

      return createdTeam;
    } catch {
      throw new Error("Unable to create profile");
    }
  }

  async function createAvatar(teamId: Guid, avatar: AvatarUploadValue) {
    if (!teamId || !avatar) return;

    try {
      return await createTeamAvatar({ teamId: teamId.value, file: avatar.file, crop: avatar.crop }).unwrap();
    } catch {
      throw new Error("Unable to create avatar");
    }
  }

  async function removeMembers(teamId: Guid) {
    for (const individual of pendingRemovals) {
      try {
        if (invitedMembers.some((m) => m.isEqualTo(individual))) {
          // Was a locally-added pending invite (never sent); inviteMembers will skip it
          continue;
        }
        const openInvitation = teamInvitations?.find((inv) => inv.inviteeId.isEqualTo(individual.id));
        if (openInvitation) {
          await cancelTeamInvitation({ invitationId: openInvitation.id.value });
          continue;
        }
        await removeTeamMember({ teamId: teamId.value, memberIndividualId: individual.id.value });
      } catch {
        throw new Error(`Unable to remove member ${individual.id}`);
      }
    }
  }

  async function inviteMembers(teamId: Guid) {
    for (const invitation of pendingInvitations) {
      if (pendingRemovals.some((r) => r.id.isEqualTo(invitation.inviteeId))) continue;
      await createTeamInvitation({
        inviteeId: invitation.inviteeId.value,
        teamId: teamId.value,
        message: invitation.message,
      });
    }
  }

  async function updateInvitationMessages(teamId: Guid) {
    for (const invitation of editedInvitations) {
      if (pendingRemovals.some((removal) => removal.id.isEqualTo(invitation.inviteeId))) continue;

      await cancelTeamInvitation({ invitationId: invitation.id.value });
      await createTeamInvitation({
        inviteeId: invitation.inviteeId.value,
        teamId: teamId.value,
        message: invitation.message,
      });
    }
  }

  async function promoteMemberToLeader(teamId: Guid) {
    if (selectedTeamLeader?.userId) {
      await promoteTeamMember({ teamId: teamId.value, individualId: selectedTeamLeader.userId.value });
    }

    setPromotingMemberToLeader(false);
  }

  /**
   * Asks before a save that would change who can see the profile, and reports what changed once it
   * has. Publishing to the marketplace is worth a sentence either side of it — the switch itself no
   * longer writes anything, so this is the only point at which it takes effect.
   */
  async function confirmVisibilityChange(): Promise<boolean> {
    if (!isVisibilityDirty) return true;

    const profileName = team?.profile?.name ?? "this";
    const response = await confirm({
      title: buildVisibilityConfirmTitle(displayedIsVisible),
      message: buildVisibilityConfirmMessage(profileName, displayedIsVisible),
    });

    return response !== ConfirmResponse.Cancel;
  }

  function handleMemberAdded(newMember: Individual) {
    if (!team) throw new Error("Team not found");
    if (invitedMembers.some((member) => member?.isEqualTo(newMember))) return;
    setInvitedMembers([...invitedMembers, newMember]);
    setPendingRemovals((prev) => prev.filter((ind) => !ind.isEqualTo(newMember)));
    setPendingInvitations([
      ...pendingInvitations,
      TeamInvitation.create({
        id: Guid.generate(),
        inviteeId: newMember.id,
        teamId: team.id,
        message: "",
        closed: false,
      }),
    ]);
  }

  function handleInvitationUpdated(updatedInvitation: TeamInvitation) {
    if (pendingInvitations.some((pendingInvitation) => pendingInvitation.id.isEqualTo(updatedInvitation.id))) {
      setPendingInvitations((prev) =>
        prev.map((pendingInvitation) =>
          pendingInvitation.id.isEqualTo(updatedInvitation.id) ? updatedInvitation : pendingInvitation,
        ),
      );
      return;
    }

    const originalInvitation = teamInvitations?.find((invitation) => invitation.id.isEqualTo(updatedInvitation.id));
    if (!originalInvitation) {
      return;
    }

    setEditedInvitations((prev) => {
      const remainingInvitations = prev.filter((invitation) => !invitation.id.isEqualTo(updatedInvitation.id));
      if (originalInvitation.message === updatedInvitation.message) {
        return remainingInvitations;
      }
      return [...remainingInvitations, updatedInvitation];
    });
  }

  function handleMemberRemoved(individual: Individual) {
    if (pendingRemovals.some((ind) => ind.isEqualTo(individual))) return;
    setPendingRemovals((prev) => [...prev, individual]);
  }

  function handleMemberRestored(individual: Individual) {
    setPendingRemovals((prev) => prev.filter((ind) => !ind.isEqualTo(individual)));
  }

  function handlePendingInvitationCanceled(individual: Individual) {
    setInvitedMembers((prev) => prev.filter((member) => !member.isEqualTo(individual)));
    setPendingInvitations((prev) => prev.filter((invitation) => !invitation.inviteeId.isEqualTo(individual.id)));
    setPendingRemovals((prev) => prev.filter((ind) => !ind.isEqualTo(individual)));
  }
  // #endregion

  // #region Render
  return (
    <MainContainer>
      <FormScrollArea data-testid="profile-form-fields">
        <HeaderContainer>
          <AvatarNameContainer>
            <AvatarUpload
              accept="image/bmp, image/gif, image/heic, image/heif, image/jpeg, image/png, image/webp"
              avatar={updatedAvatar ?? teamAvatar}
              onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
            />
            <ProfileNameInput
              name={formTeamName}
              isLoading={isLoadingTeam}
              error={(formTeamName === Config.defaultProfileName && teamId && !isLoadingTeam) || !!errorLoadingTeam}
              onNameChange={(updatedName: string) => {
                setFormTeamName(updatedName);
                if (!profile) return;
                const newProfile = new TeamProfile({
                  ...profile,
                  name: updatedName,
                });
                setProfile(newProfile);
              }}
            />
          </AvatarNameContainer>
          <ProfileCategoriesSelect
            options={Config.CompanyProfileTypes}
            categories={formCategories}
            onChange={async (updatedCategories: string[]) => {
              setFormCategories(updatedCategories);
              if (!profile) return;
              const newProfile = new TeamProfile({
                ...profile,
                categories: updatedCategories,
              });
              setProfile(newProfile);
            }}
          />
        </HeaderContainer>
        {isLoadingTeam && <Loader />}
        <>
          {!isLoadingTeam && team && (
            <EditTeamMembers
              navigate={navigate}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              team={team}
              invitations={displayedInvitations}
              invitedMembers={invitedMembers}
              pendingInvitations={pendingInvitations}
              removedIndividuals={pendingRemovals}
              onMemberAdded={handleMemberAdded}
              onInvitationUpdated={handleInvitationUpdated}
              onPendingInvitationCanceled={handlePendingInvitationCanceled}
              onMemberRestored={handleMemberRestored}
              onMemberRemoved={handleMemberRemoved}
              onLeaderReplaced={(leader) => {
                setSelectedTeamLeader(leader);
                setPromotingMemberToLeader(true);
              }}
            />
          )}

          <AboutYou>
            <Typography variant="h6">About Your Team</Typography>
            <AboutYouTextField
              value={formDescription}
              onChange={(event) => {
                setFormDescription(event.target.value);
                if (!profile) return;
                const newProfile = new TeamProfile({
                  ...profile,
                  description: event.target.value,
                });
                setProfile(newProfile);
              }}
              placeholder="Additional info displayed on your profile"
              variant="outlined"
              multiline
            />
          </AboutYou>
          {/* A member who is not the leader gets no section at all rather than an empty one: it
              occupies a grid row, and a heading over nothing reads as something that failed. */}
          {(!teamId || isTeamLeader) && (
            <FeeSchedulesSection>
              {teamId && team && isTeamLeader && <ManageTeamFeeSchedules team={team} />}
              {/* Only a form with no team yet says this. A team that is merely still loading says
                  nothing, rather than flashing "create the profile first" at someone editing one. */}
              {!teamId && (
                <>
                  <Typography variant="h6">Fee Schedules</Typography>
                  <FeeSchedulesHint variant="body2">
                    Rates belong to the team, so this opens once the profile is created. Save, and
                    the section fills in without leaving this form.
                  </FeeSchedulesHint>
                </>
              )}
            </FeeSchedulesSection>
          )}
          <TeamDetails>
            {teamId && team && (
              <VisibilityToggleSwitch
                label="Show Team On Marketplace"
                checked={displayedIsVisible}
                disabled={isSaving}
                onChange={(nextIsVisible) => {
                  setIsVisible(nextIsVisible);
                }}
              />
            )}
          </TeamDetails>
        </>
      </FormScrollArea>
      <ButtonContainer>
        <Tooltip title={saveValidationMessage} placement="top">
          <SaveButtonWrapper>
            <LoadingButton
              id="saveButton"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isProfileValidated || isSaving || (!!teamId && !hasUnsavedChanges)}
              loading={isSaving}
              color="primary"
              onClick={handleSaveButtonClick}
            >
              {teamId ? "Save Changes" : "Create Profile"}
            </LoadingButton>
          </SaveButtonWrapper>
        </Tooltip>
        {saveValidationMessage && (
          <SaveValidationMessage variant="caption">
            {saveValidationMessage}
          </SaveValidationMessage>
        )}
      </ButtonContainer>
    </MainContainer>
  );
}
// #endregion
